import React, { PropsWithChildren } from 'react'
import clsx from 'clsx'
import {
  Align as AlignStyles,
  Col as ColStyles,
  Offset as OffsetStyles,
  Order as OrderStyles,
} from '@airbus/elements/elements'
import { createUseStyles } from '../../lib/styling'
import { AlignSelf, ColumnOffset, ColumnOrder, GridSize } from '../helperTypes'
import { CommonProps } from '../helperTypes'

type Props = PropsWithChildren<{
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for all the screen sizes with the lowest priority.
   */
  xxs?: GridSize
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for the `xs` breakpoint and wider screens if not overridden.
   */
  xs?: GridSize
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for the `sm` breakpoint and wider screens if not overridden.
   */
  sm?: GridSize
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for the `md` breakpoint and wider screens if not overridden.
   */
  md?: GridSize
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for the `lg` breakpoint and wider screens if not overridden.
   */
  lg?: GridSize
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for the `xl` breakpoint and wider screens.
   */
  xl?: GridSize
  /**
   * Defines the number of grid cells the component is going to use.
   * It's applied for the `xxl` breakpoint and wider screens.
   */
  xxl?: GridSize
  /**
   * Defines the `align-self` style property.
   */
  alignSelf?: AlignSelf
  /**
   * Reorder column
   */
  order?:
    | ColumnOrder
    | {
        xxs?: ColumnOrder
        xs?: ColumnOrder
        sm?: ColumnOrder
        md?: ColumnOrder
        lg?: ColumnOrder
        xl?: ColumnOrder
        xxl?: ColumnOrder
      }
  /** Offset column */
  offset?:
    | ColumnOffset
    | {
        xxs?: ColumnOffset
        xs?: ColumnOffset
        sm?: ColumnOffset
        md?: ColumnOffset
        lg?: ColumnOffset
        xl?: ColumnOffset
        xxl?: ColumnOffset
      }
}>

export type ColProps = Props & CommonProps

const useStyles = createUseStyles(ColStyles, 'col')
const useAlignStyles = createUseStyles(AlignStyles, 'align')
const useOffsetStyles = createUseStyles(OffsetStyles, 'offset')
const useOrderStyles = createUseStyles(OrderStyles, 'order')

const Col = ({ alignSelf, children, className, offset, order, xxs, xs, sm, md, lg, xl, xxl, ...rest }: ColProps) => {
  const classes = useStyles()
  const alignClasses = useAlignStyles()
  const offsetClasses = useOffsetStyles()
  const orderClasses = useOrderStyles()

  const computedClasses: any = {}
  if (alignSelf) {
    computedClasses[alignClasses[`self-${alignSelf}`]] = true
  }
  if (order) {
    if (typeof order === 'object') {
      Object.keys(order).forEach((breakpoint) => {
        if (breakpoint === 'xxs') {
          computedClasses[orderClasses[`${(order as any)[breakpoint]}`]] = true
        } else {
          computedClasses[orderClasses[`${breakpoint}-${(order as any)[breakpoint]}`]] = true
        }
      })
    } else {
      computedClasses[orderClasses[`${order}`]] = true
    }
  }
  if (offset) {
    if (typeof offset === 'object') {
      Object.keys(offset).forEach((breakpoint) => {
        computedClasses[offsetClasses[`${breakpoint}-${(offset as any)[breakpoint]}`]] = true
      })
    } else {
      computedClasses[offsetClasses[`${offset}`]] = true
    }
  }

  return (
    <div
      className={clsx(className, {
        [classes.root]: !xxs && !xs && !sm && !md && !lg && !xl && !xxl,
        [classes[`${xxs}`]]: xxs !== undefined,
        [classes[`xs-${xs}`]]: xs !== undefined,
        [classes[`sm-${sm}`]]: sm !== undefined,
        [classes[`md-${md}`]]: md !== undefined,
        [classes[`lg-${lg}`]]: lg !== undefined,
        [classes[`xl-${xl}`]]: xl !== undefined,
        [classes[`xxl-${xxl}`]]: xxl !== undefined,
        [classes['ds-align-self-start']]: alignSelf === 'start',
        [classes['ds-align-self-center']]: alignSelf === 'center',
        [classes['ds-align-self-end']]: alignSelf === 'end',
        ...computedClasses,
      })}
      {...rest}
    >
      {children}
    </div>
  )
}

export default Col
